import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { memo, useEffect, useState } from "react";

const AnimatedBarChart = memo(() => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const chartData = [
    { label: "Jan", value: 71.28, delay: 0 },
    { label: "Fev", value: 52.78, delay: 0.1 },
    { label: "Mar", value: 57.73, delay: 0.2 },
    { label: "Abr", value: 58.21, delay: 0.3 },
    { label: "Mai", value: 61.14, delay: 0.4 },
    { label: "Jun", value: 60.82, delay: 0.5 },
  ];

  // Motion values for animated numbers
  const averageValue = useMotionValue(0);
  const growthValue = useMotionValue(0);

  // Calcula a média real dos valores
  const realAverage =
    chartData.reduce((acc, cur) => acc + cur.value, 0) / chartData.length;

  // Informações do período
  const currentYear = 2025;
  const totalMonths = chartData.length;
  const periodInfo = {
    year: currentYear,
    months: totalMonths,
    period: "1º Semestre",
    label: `${totalMonths} meses`,
    subtitle: "Jan - Jun 2025",
  };

  // Transform motion values to display values
  const displayAverage = useTransform(
    averageValue,
    (value) => `${Math.round(value)}%`,
  );
  const displayPeriod = useTransform(
    growthValue,
    (value) => `${Math.round(value)}`,
  );

  useEffect(() => {
    // Start animations after bars begin (1.5s delay + 0.8s duration)
    const timer = setTimeout(() => {
      animate(averageValue, realAverage, { duration: 1.2, ease: "easeOut" });
      animate(growthValue, totalMonths, {
        duration: 1.2,
        ease: "easeOut",
        delay: 0.2,
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [averageValue, growthValue, realAverage, totalMonths]);

  const handleMouseEnter = (index: number, event: React.MouseEvent) => {
    setHoveredIndex(index);
    setShowTooltip(true);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setTooltipPos({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setShowTooltip(false);
  };

  return (
    <motion.div
      className="relative w-full max-w-md mx-auto h-100"
      initial={{ opacity: 0, x: 30, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      viewport={{ once: true, margin: "200px" }}
    >
      <div className="relative">
        <motion.div className="absolute inset-0 rounded-3xl transform rotate-1 backdrop-blur-sm bg-gradient-to-br from-gray-50/30 to-white/20 border border-gray-100/30" />

        <div className="relative bg-gradient-to-br from-white/95 via-white/98 to-gray-50/90 rounded-3xl p-8 border border-gray-200/40 backdrop-blur-sm shadow-lg">
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-2 bg-gradient-to-r from-gray-900 via-red-800 to-gray-800 bg-clip-text text-transparent">
              Taxa de Recuperação
            </h3>
            <p className="text-sm bg-gradient-to-r from-red-600 via-gray-600 to-red-600 bg-clip-text text-transparent font-medium">
              Performance mensal (%)
            </p>
          </div>

          <div className="flex items-end justify-between h-48 space-x-3">
            {chartData.map((item, index) => (
              <motion.div
                key={item.label}
                className="flex flex-col items-center flex-1"
                animate={{
                  opacity:
                    hoveredIndex !== null && hoveredIndex !== index ? 0.3 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className="relative w-full bg-gradient-to-t from-gray-100/50 to-gray-50/30 rounded-t-lg overflow-hidden border border-gray-200/30 cursor-pointer"
                  style={{ height: "192px" }}
                  onMouseEnter={(e) => handleMouseEnter(index, e)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <motion.div
                    className="absolute bottom-0 w-full rounded-t-lg"
                    style={{
                      background: "linear-gradient(to top, #F22E3E, #BF2431)",
                    }}
                    initial={{ height: 0 }}
                    animate={{
                      height: `${item.value}%`,
                    }}
                    transition={{
                      duration: 1,
                      delay: 0.6 + item.delay,
                      ease: "easeOut",
                    }}
                  />
                </div>
                <motion.span
                  className="text-xs font-medium text-gray-500 mt-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 + item.delay }}
                >
                  {item.label}
                </motion.span>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="mt-6 flex justify-start items-center pt-4 border-t border-gray-200/60"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5 }}
          >
            <div className="text-left">
              <motion.div className="text-2xl font-bold bg-gradient-to-r from-[#F22E3E] to-[#BF2431] bg-clip-text text-transparent">
                {displayAverage}
              </motion.div>
              <div className="text-xs text-gray-400">Média Atual</div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute -bottom-6 -right-6 bg-gradient-to-br from-white/98 to-gray-50/95 rounded-2xl p-6 border border-gray-200/50 backdrop-blur-sm shadow-md"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1, type: "spring", stiffness: 200 }}
        >
          <div className="flex items-start gap-2">
            <motion.div
              className="w-3 h-3 rounded-full flex-shrink-0 mt-2"
              style={{ background: "linear-gradient(45deg, #F22E3E, #BF2431)" }}
            />
            <div className="flex flex-col space-y-1">
              <span className="text-sm font-semibold text-gray-600">
                Performance Ativa
              </span>
              <span
                className="text-xs text-gray-400"
                style={{ letterSpacing: 0.2 }}
              >
                Jan - Jun 2025
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Tooltip */}
      {showTooltip && hoveredIndex !== null && (
        <motion.div
          className="fixed z-50 pointer-events-none"
          style={{
            left: tooltipPos.x - 30,
            top: tooltipPos.y - 50,
          }}
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 10 }}
          transition={{ duration: 0.15 }}
        >
          <div className="bg-gray-900/90 backdrop-blur-sm text-white px-3 py-2 rounded-lg shadow-lg border border-gray-700/50">
            <div className="text-sm font-semibold">
              {chartData[hoveredIndex].value.toFixed(1)}%
            </div>
            <div className="text-xs text-gray-300 mt-0.5">
              {chartData[hoveredIndex].label}
            </div>
            {/* Seta do tooltip */}
            <div className="absolute top-full left-1/2 transform -translate-x-1/2">
              <div className="border-4 border-transparent border-t-gray-900/90"></div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
});

AnimatedBarChart.displayName = "AnimatedBarChart";

export default AnimatedBarChart;
